import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { HOME_THEME, MONO, SANS } from "./theme";
import { Glyph, Kicker, type GlyphName } from "./Parts";

/**
 * The pieces a page needs to sit in the home look outside the home itself:
 * the results pages (public and in the app) use these so a visitor who starts
 * on the home page never crosses into the older navy styling on the way to
 * their report.
 */

export const BRAND_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap";

/**
 * Geist and Geist Mono are self-hosted and declared in index.html, so every page
 * has them from the first paint. Kept as a no-op for the pages that still
 * render it; it used to add a render-blocking Google Fonts stylesheet.
 */
export function BrandFonts() {
  return null;
}

/**
 * Redeclares the theme tokens for everything inside it, the same way the home
 * does. `fill` makes it cover at least the viewport, which is what a full page
 * wants; inside the app shell it fills the content column beside the sidebar.
 */
export function BrandPage({ children, className, fill = true }: { children: ReactNode; className?: string; fill?: boolean }) {
  return (
    <div
      className={cn("overflow-x-clip bg-background text-foreground antialiased", fill && "min-h-screen", className)}
      style={{ ...HOME_THEME, fontFamily: SANS }}
    >
      <BrandFonts />
      {children}
    </div>
  );
}

type Variant = "primary" | "ghost" | "quiet";
type Size = "md" | "sm";

const VARIANT: Record<Variant, string> = {
  primary: "ec-cta font-semibold",
  ghost:
    "border border-[hsl(var(--ec-line-strong))] font-medium text-foreground hover:border-[hsl(var(--ec-mint)/0.45)] hover:bg-foreground/[0.03]",
  quiet: "font-medium text-[hsl(var(--ec-text-2))] hover:text-foreground",
};

const SIZE: Record<Size, string> = {
  md: "h-11 gap-2 rounded-[10px] px-5 text-[14.5px]",
  sm: "h-9 gap-1.5 rounded-[9px] px-3.5 text-[13px]",
};

export function BrandButton({
  variant = "primary",
  size = "md",
  glyph,
  glyphEnd,
  className,
  children,
  type = "button",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; glyph?: GlyphName; glyphEnd?: GlyphName }) {
  const g = size === "sm" ? 14 : 15;
  return (
    <button
      type={type}
      className={cn(
        "inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        SIZE[size],
        variant === "quiet" && "px-1",
        VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {glyph && <Glyph name={glyph} size={g} />}
      {children}
      {glyphEnd && <Glyph name={glyphEnd} size={g} />}
    </button>
  );
}

/** A thin ring in the brand green. Motion is the only thing it adds, so it stays small. */
export function Spinner({ label, size = 36 }: { label: string; size?: number }) {
  return (
    <div role="status" className="flex flex-col items-center gap-4">
      <span
        aria-hidden
        className="animate-spin rounded-full border-2 border-foreground/[0.08] border-t-primary motion-reduce:animate-none"
        style={{ width: size, height: size }}
      />
      <span className="text-[12px] uppercase tracking-[0.14em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
        {label}
      </span>
    </div>
  );
}

/**
 * A page state that is not the report: failed, nothing to show, locked.
 * Glyph in a ring, a mono kicker for context (usually the domain), the
 * heading, one line of explanation and the way forward.
 */
export function StateBlock({
  glyph,
  kicker,
  title,
  body,
  children,
  className,
}: {
  glyph?: GlyphName;
  kicker?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-auto flex max-w-[560px] flex-col items-center text-center", className)}>
      {glyph && (
        <span className="mb-7 grid h-14 w-14 place-items-center rounded-full border border-[hsl(var(--ec-line-strong))] text-[hsl(var(--ec-mint))]">
          <Glyph name={glyph} size={22} />
        </span>
      )}
      {kicker && <Kicker className="mb-3">{kicker}</Kicker>}
      <h1 className="text-[32px] font-semibold leading-[1.08] tracking-[-0.035em] sm:text-[40px]">{title}</h1>
      {body && <p className="mt-4 max-w-[460px] text-[16px] leading-relaxed text-[hsl(var(--ec-text-2))]">{body}</p>}
      {children && <div className="mt-8 flex flex-wrap items-center justify-center gap-3">{children}</div>}
    </div>
  );
}

/** Host of a URL without the www, for kickers and titles. Never throws. */
export function hostOf(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
